'use strict';

/**
 * T451 P3.1 — the BROWSER half of the tasks pilot: unseal the at-rest task-store
 * envelope under the user's master key and produce the SAME state the warm-key
 * route used to produce server-side.
 *
 * T451 P3.4 — and the WRITE half: `sealTaskStore` seals the whole (mutated)
 * store back under the same key, shaped exactly as the server sink writes it
 * (task-state.js `buildTaskStorePayload`: narrowed to the owner, `updatedAt`
 * stamped, hybrid-PQ wrapped like index.js `writeUserTaskStore` /
 * task-file-store.js `writeTaskStore` — T347 P5 LD-3). The server stores that
 * envelope VERBATIM (task-lists-sealed-write-route.js) and never opens it.
 *
 * Mirrors shared/crypto/media-editor-seal.js (the shipped browser-only-decrypt
 * exemplar): the master key is read from the live vault session
 * (`session._identityMaster`, page-lifetime, never storage) through an injected
 * resolver; the key never leaves this closure; every failure is TYPED and
 * non-throwing so the client can name it to the user.
 *
 * Delivered via shared/crypto/browser-bundle-entry.js as
 * `window.OysterVault.unsealTaskStore(envelope, { ownerKey, fallbackKeys })` and
 * `window.OysterVault.sealTaskStore(store, { recordId, ownerKey, fallbackKeys })`.
 *
 * unsealTaskStore result (three-state, never a bare null):
 *   { status:'ok',      state, store, updatedAt }   — `state`: the render clone,
 *                                normalized exactly as index.js does; `store`: the
 *                                FULL parsed store (auto lists included) the
 *                                write must mutate and re-seal — never the clone
 *   { status:'absent',  state, store, updatedAt:null } — envelope null ⇒ "no tasks yet"
 *   { status:'locked' }                      — no unlocked vault session (no master key in page)
 *   { status:'sealed-under-a-different-key-or-tampered' } — decrypt refused (non-throwing null)
 *   { status:'not-a-task-store-envelope' }   — recordType binding failed (a swapped record)
 *   { status:'owner-key-invalid' }           — the server-declared owner key is unusable
 *   { status:'malformed-plaintext' }         — decrypted bytes are not a store
 *
 * sealTaskStore result:
 *   { status:'ok', envelope, updatedAt }     — a task-store envelope bound to `recordId`
 *   { status:'locked' }                      — no master key in the page
 *   { status:'record-scope-required' }       — no recordId: never seals under a shared DEK (S653)
 *   { status:'invalid-store' }               — not a store object
 */

const { encryptUnderMasterKey, decryptUnderMasterKey } = require('../../crypto/master-key-encryption.js');
const { normalizeTaskStateForUser, buildTaskStorePayload } = require('./task-state.js');

const TASK_STORE_RECORD_TYPE = 'task-store';

function emptyStore() { return { version: 1, users: {}, updatedAt: null }; }

function normalizeOrInvalid(store, ownerKey, fallbackKeys) {
  try {
    return { state: normalizeTaskStateForUser(store, ownerKey, { fallbackKeys }) };
  } catch (_e) {
    return { invalid: true };
  }
}

/**
 * @param {object} opts
 * @param {function(): (object|null)} opts.resolveSession — returns the live vault
 *   session ({ _identityMaster: 32-byte key }) or null when locked.
 */
function createTaskStoreUnsealAgent({ resolveSession } = {}) {
  function masterKey() {
    let session = null;
    try { session = typeof resolveSession === 'function' ? resolveSession() : null; } catch (_e) { session = null; }
    const key = session && session._identityMaster;
    return (key && key.length === 32) ? key : null;
  }

  function unsealTaskStore(envelope, { ownerKey, fallbackKeys } = {}) {
    const fb = Array.isArray(fallbackKeys) ? fallbackKeys : [];
    if (envelope == null) {
      const n = normalizeOrInvalid(emptyStore(), ownerKey, fb);
      if (n.invalid) return { status: 'owner-key-invalid' };
      return { status: 'absent', state: n.state, store: emptyStore(), updatedAt: null };
    }
    if (typeof envelope !== 'object' || envelope.recordType !== TASK_STORE_RECORD_TYPE) {
      return { status: 'not-a-task-store-envelope' };
    }
    const key = masterKey();
    if (!key) return { status: 'locked' };
    const plain = decryptUnderMasterKey({ envelope, userMasterKey: key });
    if (plain == null) return { status: 'sealed-under-a-different-key-or-tampered' };
    let parsed;
    try { parsed = JSON.parse(plain); } catch (_e) { return { status: 'malformed-plaintext' }; }
    if (!parsed || typeof parsed !== 'object') return { status: 'malformed-plaintext' };
    const store = {
      version: parsed.version ?? 1,
      users: parsed.users && typeof parsed.users === 'object' ? parsed.users : {},
      updatedAt: parsed.updatedAt || null,
    };
    const n = normalizeOrInvalid(store, ownerKey, fb);
    if (n.invalid) return { status: 'owner-key-invalid' };
    return { status: 'ok', state: n.state, store, updatedAt: store.updatedAt };
  }

  function sealTaskStore(store, { recordId, ownerKey, fallbackKeys } = {}) {
    if (!store || typeof store !== 'object') return { status: 'invalid-store' };
    const rid = recordId == null ? '' : String(recordId).trim();
    if (!rid) return { status: 'record-scope-required' };
    const key = masterKey();
    if (!key) return { status: 'locked' };
    const payload = buildTaskStorePayload(store, ownerKey, Array.isArray(fallbackKeys) ? fallbackKeys : []);
    const envelope = encryptUnderMasterKey({
      plaintext: JSON.stringify(payload),
      userMasterKey: key,
      recordType: TASK_STORE_RECORD_TYPE,
      recordId: rid,
      // T347 P5 (LD-3): tasks are per-user content → master-key + ML-KEM-768
      // hybrid PQ, exactly like the server write path.
      hybridWrap: true,
    });
    return { status: 'ok', envelope, updatedAt: payload.updatedAt };
  }

  return { unsealTaskStore, sealTaskStore, TASK_STORE_RECORD_TYPE };
}

module.exports = { createTaskStoreUnsealAgent, TASK_STORE_RECORD_TYPE };
